import { EChartsCoreOption, SetOptionOpts } from 'echarts';
import { ComponentModel } from 'echarts/core';

type CreateReplaceMergeOptionResponse =
  | undefined
  | {
      /**
       * Updated EChart `option` to pass as the first argument to `setOption`.
       */
      option: EChartsCoreOption;
      /**
       * Configuration options passed as the second argument to `setOption` that
       * informs ECharts how to handle the options update.
       */
      opts: SetOptionOpts;
    };

type GenerateOptionUpdateArgsOpts = {
  themeChanged?: boolean;
};

/**
 * Looks at the previous and next `option` and generates an updated option and
 * configuration that can be used to call `setOption` in "replace merge" mode by
 * doing the following:
 * - Comparing the top-level properties of each object for reference equality.
 * - Properties that are identical across previous/next are excluded.
 * - Properties that are new in next are included.
 * - Properties that were removed in next are set to `null` to ensure they will
 *   be removed.
 *
 * This function **does not** use deep equals checks because they are expensive
 * when run frequently against large, complex objects and can counterproductively
 * increase performance issues instead of decreasing them.
 *
 * See echarts documentation for more information about "replace merge" mode.
 * https://echarts.apache.org/en/api.html#echartsInstance.setOption
 */
export function generateOptionUpdateArgs(
  prevOption: EChartsCoreOption,
  nextOption: EChartsCoreOption,
  { themeChanged }: GenerateOptionUpdateArgsOpts
): CreateReplaceMergeOptionResponse {
  const optionKeys = new Set(Object.keys(prevOption).concat(Object.keys(nextOption)));

  const toUpdateOpt: EChartsCoreOption = {};

  // We **only** pass along the options that changed to `setOption`, so that we
  // can avoid an expensive "clone" operation inside the echarts code for large
  // complex objects (e.g. timeseries data) when they have not changed.
  // https://github.com/apache/echarts/blob/b37d67cbd47534eb3307262d64be9cdae3325609/src/model/OptionManager.ts#L108-L111
  for (const optionKey of optionKeys) {
    const prevProp = prevOption[optionKey];
    const nextProp = nextOption[optionKey];

    const hasPrevProp = typeof prevProp !== 'undefined';
    const hasNextProp = typeof nextProp !== 'undefined';

    if (hasPrevProp && !hasNextProp) {
      // Option property was removed. Set to null to ensure it gets removed.
      toUpdateOpt[optionKey] = null;
    } else if (!hasPrevProp && hasNextProp) {
      // Option property was added. Include it as is.
      toUpdateOpt[optionKey] = nextProp;
    } else if (hasPrevProp && hasNextProp && prevProp !== nextProp) {
      // Option property changed. Use the new value.
      toUpdateOpt[optionKey] = nextProp;
    }

    // A full update is required for the following option use cases:
    // - options that do not support replaceMerge.
    // - `dataset`: otherwise echarts tries to merge the old/new dataset and it can lead to unexpected
    //   results.
    const optionRequiresFullUpdate =
      toUpdateOpt[optionKey] !== undefined && (!ComponentModel.hasClass(optionKey) || optionKey === 'dataset');

    // We do a full update when the theme changes because otherwise the time series chart can
    // get in a weird state.
    if (optionRequiresFullUpdate || themeChanged) {
      // If the `dataset` option is modified, we do a full update without merging
      // because otherwise we can see some weird behavior when datasets are merged.
      //
      // We also do a full update if the property is not supported for replaceMerge.
      // See related context in ECharts issues & code:
      // https://github.com/apache/echarts/issues/18588
      // https://github.com/apache/echarts/blob/b37d67cbd47534eb3307262d64be9cdae3325609/src/model/Global.ts#L1071-L1075
      return {
        option: nextOption,
        opts: {
          notMerge: true,
        },
      };
    }

    // We do nothing if the property was undefined for before/after or if the
    // before/after have reference equality (deep equality checks on complex objects
    // like datasets are very expensive to do so frequently).
  }

  const toUpdateKeys = Object.keys(toUpdateOpt);

  if (!toUpdateKeys.length) {
    // Nothing changed. Return undefined to communicate that `setOption` should
    // not be called.
    return;
  }

  return {
    option: toUpdateOpt,
    opts: {
      replaceMerge: toUpdateKeys,
    },
  };
}

/**
 * Helper function for debugging option updates. Returns context that can be
 * console.log-ed when debugging.
 */
export function debugOptionUpdateArgs(optionArgs: CreateReplaceMergeOptionResponse): [string, ...unknown[]] {
  if (optionArgs?.opts.replaceMerge) {
    // We're doing a partial update. Return the names of the top-level options
    // that will be updated.
    return ['partial update', optionArgs?.opts.replaceMerge, optionArgs];
  }
  if (optionArgs?.opts.notMerge) {
    // We're doing a full update.
    return ['full update', optionArgs];
  }

  // Coverage just in case for edge cases we hopefully should not have.
  return ['unknown update', optionArgs];
}

const mouseEvents = [
  'click',
  'dblclick',
  'mousedown',
  'mousemove',
  'mouseup',
  'mouseover',
  'mouseout',
  'globalout',
  'contextmenu',
] as const;
export type MouseEventName = (typeof mouseEvents)[number];
export function isEChartMouseEvent(eventName: EventName): eventName is MouseEventName {
  return (mouseEvents as readonly string[]).includes(eventName);
}

const batchEvents = ['datazoom', 'downplay', 'highlight'] as const;
export type BatchEventName = (typeof batchEvents)[number];
export function isEChartBatchEvent(eventName: EventName): eventName is BatchEventName {
  return (batchEvents as readonly string[]).includes(eventName);
}

export type ChartEventName = 'finished';

export type EventName = MouseEventName | ChartEventName | BatchEventName;

type ModelFinderIndexQuery = number | number[] | 'all' | 'none' | false;
type ModelFinderIdQuery = string | number | Array<string | number>;
type ModelFinderNameQuery = string | number | Array<string | number>;
type ModelFinderObject = {
  seriesIndex?: ModelFinderIndexQuery;
  seriesId?: ModelFinderIdQuery;
  seriesName?: string | number | Array<string | number>;
  geoIndex?: ModelFinderIndexQuery;
  geoId?: ModelFinderIdQuery;
  geoName?: ModelFinderNameQuery;
  bmapIndex?: ModelFinderIndexQuery;
  bmapId?: ModelFinderIdQuery;
  bmapName?: ModelFinderNameQuery;
  xAxisIndex?: ModelFinderIndexQuery;
  xAxisId?: ModelFinderIdQuery;
  xAxisName?: ModelFinderNameQuery;
  yAxisIndex?: ModelFinderIndexQuery;
  yAxisId?: ModelFinderIdQuery;
  yAxisName?: ModelFinderNameQuery;
  gridIndex?: ModelFinderIndexQuery;
  gridId?: ModelFinderIdQuery;
  gridName?: ModelFinderNameQuery;
  dataIndex?: number;
  dataIndexInside?: number;
};
export type ModelFinder = string | ModelFinderObject;
